import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchCfoComplianceScore,
  fetchCfoCoverageHeatmap,
  fetchCfoOpenIssues,
  fetchCfoProgramProgress,
  fetchCfoRepeatEscalated,
  fetchCfoRiskLandscape,
  fetchCfoSOCCoverage,
  fetchCfoScoreTrend,
  type CFOComplianceScoreResponse,
  type CFOCoverageHeatmapRow,
  type CFOOpenIssuesResponse,
  type CFOProgramProgressRow,
  type CFORepeatEscalatedRow,
  type CFORiskLandscapeRow,
  type CFOSOCCoverageRow,
  type CFOScoreTrendRow,
} from '../../request/analytics/cfoReports.ts';
import { analyticsCfoQueryKeys } from '../../queries/analytics/keys.ts';

/**
 * React Query v5 hooks for the CFO/Executive-persona analytics dashboard
 * (Feature 5.3, reports #1-#8). Each report is its own independent query
 * so a single failing/slow widget doesn't block the rest of the dashboard.
 */

export function useCfoComplianceScore(): UseQueryResult<CFOComplianceScoreResponse | null, Error> {
  return useQuery({
    queryKey: analyticsCfoQueryKeys.complianceScore(),
    queryFn: fetchCfoComplianceScore,
  });
}

export function useCfoCoverageHeatmap(): UseQueryResult<CFOCoverageHeatmapRow[], Error> {
  return useQuery({
    queryKey: analyticsCfoQueryKeys.coverageHeatmap(),
    queryFn: fetchCfoCoverageHeatmap,
  });
}

export function useCfoOpenIssues(): UseQueryResult<CFOOpenIssuesResponse, Error> {
  return useQuery({
    queryKey: analyticsCfoQueryKeys.openIssues(),
    queryFn: fetchCfoOpenIssues,
  });
}

export function useCfoProgramProgress(): UseQueryResult<CFOProgramProgressRow[], Error> {
  return useQuery({
    queryKey: analyticsCfoQueryKeys.programProgress(),
    queryFn: fetchCfoProgramProgress,
  });
}

export function useCfoSOCCoverage(): UseQueryResult<CFOSOCCoverageRow[], Error> {
  return useQuery({
    queryKey: analyticsCfoQueryKeys.socCoverage(),
    queryFn: fetchCfoSOCCoverage,
  });
}

export function useCfoRiskLandscape(): UseQueryResult<CFORiskLandscapeRow[], Error> {
  return useQuery({
    queryKey: analyticsCfoQueryKeys.riskLandscape(),
    queryFn: fetchCfoRiskLandscape,
  });
}

export function useCfoRepeatEscalated(): UseQueryResult<CFORepeatEscalatedRow[], Error> {
  return useQuery({
    queryKey: analyticsCfoQueryKeys.repeatEscalated(),
    queryFn: fetchCfoRepeatEscalated,
  });
}

export function useCfoScoreTrend(): UseQueryResult<CFOScoreTrendRow[], Error> {
  return useQuery({
    queryKey: analyticsCfoQueryKeys.scoreTrend(),
    queryFn: fetchCfoScoreTrend,
  });
}
